import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { PropertyDetailBody } from "@/pages/Properties";
import BrandedHero from "@/components/BrandedHero";
import StatusFlag from "@/components/StatusFlag";
import PropertyStreetViewCard from "@/components/PropertyStreetViewCard";
import PastInspectionsStrip from "@/components/PastInspectionsStrip";
import PropertyEnrichCard from "@/components/PropertyEnrichCard";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

/**
 * PropertyDetailV2 — CP-style property detail, real data.
 *
 * LEFT (2/3): Snapshot · Units grid · Work Orders list · Tenants
 * RIGHT (1/3): Quick actions · Landlord · Compliance · Money · More drawer
 *
 * Classic V1 body available inside the "All actions & details" drawer.
 * `?v=classic` still renders V1.
 */
export default function PropertyDetailV2() {
  const { address: encoded } = useParams();
  const address = decodeURIComponent(encoded || "");
  const [data, setData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [syncingCU, setSyncingCU] = useState(false);
  const [syncCUProgress, setSyncCUProgress] = useState("");
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [startingInspection, setStartingInspection] = useState(false);
  const navigate = useNavigate();

  // "Start Inspection Radar" — creates a fresh inspection scoped to this
  // property and jumps to the wizard. Uses the address-based helper so we
  // don't need to fish out the property ObjectId from `data`.
  const startInspection = React.useCallback(async () => {
    if (!address || startingInspection) return;
    setStartingInspection(true);
    try {
      const { data: insp } = await api.post("/prop-inspections-by-address", { address });
      navigate(`/admin/inspections/${insp.inspection_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start inspection");
    } finally {
      setStartingInspection(false);
    }
  }, [address, startingInspection, navigate]);

  // Reusable loader so the Sync CU tile can refresh property + WO data
  // after a batch sync completes.
  const load = React.useCallback(() => {
    let alive = true;
    api.get("/properties/detail", { params: { address } })
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ found: false }); });
    return () => { alive = false; };
  }, [address]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  const s = data?.summary || {};
  const units = data?.units || [];
  const jobs = data?.jobs || [];
  const customers = data?.customers || [];
  const compliance = data?.compliance || {};
  const openJobs = jobs.filter(j => j.status !== "completed" && j.status !== "canceled");
  const closedJobs = jobs.filter(j => j.status === "completed" || j.status === "canceled");

  const chips = [];
  if (data) {
    if (s.open_jobs != null)    chips.push({ icon: "🧾", label: `${s.open_jobs} open` });
    if (s.total_jobs != null)   chips.push({ icon: "📚", label: `${s.total_jobs} lifetime` });
    if (units.length)           chips.push({ icon: "🏢", label: `${units.length} unit${units.length === 1 ? "" : "s"}` });
    if (s.total_turns)          chips.push({ icon: "🔄", label: `${s.total_turns} turns` });
    if (s.chargeback_total)     chips.push({ icon: "💰", label: `$${Math.round(s.chargeback_total).toLocaleString()} chargeback` });
  }

  const openMap = () => window.open(`https://www.google.com/maps/search/${encodeURIComponent(address)}`, "_blank");
  const openStreet = () => window.open(`https://www.google.com/maps?q=&layer=c&cbll=${encodeURIComponent(address)}`, "_blank");

  if (!data) {
    return <AdminShell><BrandedHero title="Loading property…" size="sm"/></AdminShell>;
  }

  if (data.found === false) {
    return (
      <AdminShell>
        <BrandedHero title="Property not found" subtitle={address} size="sm"/>
        <div className="max-w-3xl mx-auto p-8 text-center">
          <Link to="/admin/properties" className="text-[#00807a] font-bold text-sm">← All properties</Link>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell>
      <BrandedHero
        breadcrumbs={[
          { to: "/admin?v=v2", label: "← Dashboard" },
          { to: "/admin/properties", label: "Properties" },
          { label: address },
        ]}
        eyebrow={data.former ? "Former property" : "Property"}
        title={address}
        subtitle={
          <>
            {data.landlord?.name && (
              <>Owner{" "}
                {data.landlord.id ? (
                  <Link
                    to={`/admin/landlord/${data.landlord.id}`}
                    data-testid="v2p-hero-landlord-link"
                    className="text-white underline decoration-white/40 hover:decoration-white font-black">
                    {data.landlord.name}
                  </Link>
                ) : (
                  <b className="text-white">{data.landlord.name}</b>
                )}
              </>
            )}
            {customers.length > 0 && <> · {customers.length} customer{customers.length === 1 ? "" : "s"}</>}
          </>
        }
        chips={chips}
        size="md"
        actions={
          <>
            <button onClick={() => setTimelineOpen(true)}
              data-testid="v2-prop-timeline-btn"
              className="h-9 px-3 rounded-full bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0F1F3A] text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
              📸 Photo Timeline
            </button>
            <button onClick={() => setRenameOpen(true)}
              data-testid="v2-prop-rename-btn"
              title="Correct a wrong or misspelled property address"
              className="h-9 px-3 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
              ✏️ Edit Address
            </button>
            <Link to={`/admin/properties/${encodeURIComponent(address)}?v=classic`}
              data-testid="v2-prop-classic-toggle"
              className="h-9 px-3 rounded-full bg-white/15 backdrop-blur text-white text-[10px] font-black uppercase tracking-widest hover:bg-white/25 border border-white/20 inline-flex items-center">
              Use Classic
            </Link>
          </>
        }
      />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 -mt-16 relative z-10 pb-6">
        {/* Property street-view + block map — restored from V1 for visual ID */}
        <div className="mb-4" data-testid="v2p-streetview">
          <PropertyStreetViewCard address={address}/>
        </div>

        {/* AI enrich — Philly OPA + AI rent comps. Same green dashed strip
            techs know from the Unit page. Renders at building level so any
            unit at this address inherits the pulled facts. */}
        <div className="mb-4" data-testid="v2p-enrich">
          <PropertyEnrichCard address={address}/>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* LEFT (2/3) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Big-picture stats strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="v2p-stats">
              <StatTile label="Total WOs"      value={s.total_jobs ?? 0}      color="#00C4CC" to={`/admin/jobs?property=${encodeURIComponent(address)}`}/>
              <StatTile
                label="Open WOs"
                value={s.open_jobs ?? 0}
                color="#FF6A2B"
                testid="v2p-stat-open-wos"
                to={
                  // If there's exactly one open WO, jump straight to it —
                  // saves a click when the list would only show a single row.
                  openJobs.length === 1
                    ? `/admin/jobs/${openJobs[0].id}?v=v2`
                    : `/admin/jobs?property=${encodeURIComponent(address)}&status=open`
                }/>
              <StatTile label="Turns done"     value={s.total_turns ?? 0}     color="#B12FBB"/>
              <StatTile label="Chargeback $"   value={`$${Math.round(s.chargeback_total || 0).toLocaleString()}`} color="#22C6A6"/>
            </div>

            {/* Units grid */}
            {units.length > 0 && (
              <Card testId="v2p-units">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-lg font-black text-[#0F1F3A]">Units</h3>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{units.length}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {units.map(u => (
                    <Link key={u.unit} to={`/admin/properties/${encodeURIComponent(address)}?u=${encodeURIComponent(u.unit)}&v=classic`}
                      data-testid={`v2p-unit-${u.unit}`}
                      className="p-3 rounded-xl border-2 border-slate-100 hover:border-[#00C4CC] hover:bg-[#00C4CC]/5 transition-colors">
                      <div className="flex items-center gap-1.5">
                        <span className="text-lg">🚪</span>
                        <span className="text-sm font-black text-[#0F1F3A]">Unit {u.unit}</span>
                        {u.has_ring_alarm && <span className="text-[10px]" title="Ring alarm">🔔</span>}
                      </div>
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                        {u.open > 0 && (
                          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                            style={{ background: "#FF6A2B22", color: "#FF6A2B" }}>
                            {u.open} open
                          </span>
                        )}
                        {u.jobs > 0 && u.open === 0 && (
                          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-slate-100 text-[#8B93A7]">
                            {u.jobs} lifetime
                          </span>
                        )}
                        {u.turns > 0 && (
                          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                            style={{ background: "#B12FBB22", color: "#B12FBB" }}>
                            {u.turns} turn{u.turns === 1 ? "" : "s"}
                          </span>
                        )}
                        {u.vacancy_status === "vacant" && (
                          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                            style={{ background: "#F5B93F22", color: "#8B6D1F" }}>Vacant</span>
                        )}
                      </div>
                      {u.lockbox_code && (
                        <div className="mt-1.5 text-[10px] font-medium text-[#8B93A7]">🔒 {u.lockbox_code}</div>
                      )}
                    </Link>
                  ))}
                </div>
              </Card>
            )}

            {/* Previous completed inspections — closes the loop from Recommend tab. */}
            <PastInspectionsStrip address={address}/>

            {/* Open Work Orders */}
            <Card testId="v2p-open-jobs">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-black text-[#0F1F3A]">Open Work Orders</h3>
                <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{openJobs.length}</span>
                <Link to={`/admin/jobs?property=${encodeURIComponent(address)}`}
                  className="ml-auto text-[10px] font-black uppercase tracking-widest text-[#00807a] hover:text-[#0F1F3A]">
                  All jobs →
                </Link>
              </div>
              {openJobs.length === 0 ? (
                <div className="text-center text-[11px] text-[#8B93A7] font-medium py-4">Nothing open · nice work 🎉</div>
              ) : (
                <div className="space-y-1">
                  {openJobs.map(j => (
                    <Link key={j.id} to={`/admin/jobs/${j.id}?v=v2`}
                      data-testid={`v2p-job-${j.id}`}
                      className="flex items-center gap-3 p-2.5 hover:bg-slate-50 rounded-lg group transition-colors">
                      <StatusFlag status={j.status} size="xs"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-black text-[#0F1F3A] truncate">
                          <span className="text-[#8B93A7] mr-2 font-black">#{j.work_order_number || j.id.slice(0, 6)}</span>
                          {j.title}
                        </div>
                        <div className="text-[10px] font-medium text-[#8B93A7] truncate">
                          {j.unit && <>Unit {j.unit} · </>}
                          {j.assigned_to_name || <span className="text-[#F97316]">Unassigned</span>}
                          {j.priority && (j.priority === "urgent" || j.priority === "critical" || j.priority === "high") && <> · 🔥 {j.priority}</>}
                        </div>
                      </div>
                      <span className="text-[#8B93A7] group-hover:text-[#0F1F3A] text-lg leading-none">→</span>
                    </Link>
                  ))}
                </div>
              )}
            </Card>

            {/* Recent Closed WOs */}
            {closedJobs.length > 0 && (
              <Card testId="v2p-closed-jobs">
                <details className="group">
                  <summary className="cursor-pointer list-none flex items-center gap-2">
                    <span className="group-open:rotate-90 transition-transform inline-block text-[#8B93A7]">▶</span>
                    <h3 className="text-lg font-black text-[#0F1F3A]">Completed Work Orders</h3>
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{closedJobs.length}</span>
                  </summary>
                  <div className="mt-3 space-y-1">
                    {closedJobs.slice(0, 15).map(j => (
                      <Link key={j.id} to={`/admin/jobs/${j.id}?v=v2`} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg">
                        <StatusFlag status={j.status} size="xs"/>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-[#0F1F3A] truncate">
                            <span className="text-[#8B93A7] mr-1.5">#{j.work_order_number || j.id.slice(0, 6)}</span>
                            {j.title}
                          </div>
                        </div>
                        <span className="text-[10px] font-medium text-[#8B93A7]">{fmtDate(j.created_at)}</span>
                      </Link>
                    ))}
                    {closedJobs.length > 15 && (
                      <Link to={`/admin/jobs?property=${encodeURIComponent(address)}`} className="block text-center text-[10px] font-black uppercase tracking-widest text-[#00807a] hover:text-[#0F1F3A] pt-2">
                        View all {closedJobs.length} completed →
                      </Link>
                    )}
                  </div>
                </details>
              </Card>
            )}

            {/* Notes / Property notes */}
            {data.notes && (
              <Card testId="v2p-notes">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-black text-[#0F1F3A]">Property Notes</h3>
                  {data.notes_updated_by_name && (
                    <span className="text-[10px] font-medium text-[#8B93A7]">
                      · {data.notes_updated_by_name} · {fmtDate(data.notes_updated_at)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#0F1F3A] whitespace-pre-wrap leading-relaxed">{data.notes}</p>
              </Card>
            )}
          </div>

          {/* RIGHT (1/3) */}
          <div className="space-y-4">
            {/* Quick actions */}
            <Card testId="v2p-quick-actions" pad="sm">
              <div className="flex items-center gap-2 mb-2 px-1">
                <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">Quick actions</div>
                <button
                  onClick={() => setDrawerOpen(true)}
                  data-testid="v2p-open-drawer"
                  className="ml-auto h-6 px-2 rounded-full bg-[#0F1F3A] text-white text-[9px] font-black uppercase tracking-widest hover:bg-[#1a2d4f]"
                >
                  ⋮ More
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1">
                <ActionTile icon="🗺" label="Map"        onClick={openMap}/>
                <ActionTile icon="👁"  label="Street"     onClick={openStreet}/>
                <ActionTile icon="📄" label="Snapshot"   onClick={() => setDrawerOpen(true)}/>
                <ActionTile icon="➕" label="New WO"     onClick={() => window.location.href = `/admin/jobs?new=1&v=classic&property=${encodeURIComponent(address)}`}/>
                <ActionTile
                  icon="🔍"
                  label={startingInspection ? "Starting…" : "Inspection"}
                  onClick={startInspection}
                  disabled={startingInspection}
                  testid="pdv2-action-inspection"
                />
                <ActionTile
                  icon="🔄"
                  label={syncingCU ? `Sync… ${syncCUProgress}` : "Sync CU"}
                  onClick={async () => {
                    if (syncingCU) return;
                    if (openJobs.length === 0) { toast.info("No open WOs to sync"); return; }
                    setSyncingCU(true);
                    setSyncCUProgress(`0/${openJobs.length}`);
                    let done = 0, ok = 0, fail = 0;
                    const results = await Promise.all(openJobs.map(j =>
                      api.post(`/clickup/jobs/${j.id}/sync-now`)
                        .then(() => { ok++; done++; setSyncCUProgress(`${done}/${openJobs.length}`); })
                        .catch(() => { fail++; done++; setSyncCUProgress(`${done}/${openJobs.length}`); })
                    ));
                    setSyncingCU(false);
                    setSyncCUProgress("");
                    if (fail === 0) toast.success(`ClickUp synced · ${ok} WO${ok === 1 ? "" : "s"}`);
                    else toast.error(`Synced ${ok} · ${fail} failed`);
                    // Refresh the property data so any status/comment changes appear
                    load();
                  }}
                />
                <ActionTile icon="🖨" label="Print"      onClick={() => window.print()}/>
                <ActionTile icon="↗️" label="V1"         onClick={() => window.location.href = `/admin/properties/${encodeURIComponent(address)}?v=classic`}/>
              </div>
            </Card>

            {/* Landlord card */}
            {data.landlord && (
              <Card testId="v2p-landlord">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#B12FBB] to-[#5B3AE0] flex items-center justify-center text-white font-black text-xs flex-shrink-0">
                    {initials(data.landlord.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">Owner</div>
                    <div className="text-sm font-black text-[#0F1F3A] truncate">{data.landlord.name}</div>
                  </div>
                </div>
                {data.landlord.email && (
                  <div className="text-[11px] text-[#0F1F3A]">
                    <a href={`mailto:${data.landlord.email}`} className="hover:underline">{data.landlord.email}</a>
                  </div>
                )}
                {data.landlord.phone && (
                  <div className="text-[11px] text-[#0F1F3A] mt-1">
                    <a href={`tel:${data.landlord.phone}`} className="hover:underline">{data.landlord.phone}</a>
                  </div>
                )}
              </Card>
            )}

            {/* Compliance */}
            {(compliance.licenses_count > 0 || compliance.expiring_soon > 0 || compliance.expired > 0) && (
              <Card testId="v2p-compliance">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-black text-[#0F1F3A]">Compliance</h3>
                </div>
                <div className="space-y-2">
                  {compliance.licenses_count != null && (
                    <ComplianceRow label="Licenses"   value={compliance.licenses_count} color="#3B82F6"/>
                  )}
                  {compliance.expiring_soon > 0 && (
                    <ComplianceRow label="Expiring soon" value={compliance.expiring_soon} color="#F5B93F"/>
                  )}
                  {compliance.expired > 0 && (
                    <ComplianceRow label="Expired"    value={compliance.expired} color="#E64C5A"/>
                  )}
                </div>
              </Card>
            )}

            {/* Assets / customers */}
            {customers.length > 0 && (
              <Card testId="v2p-customers">
                <h3 className="text-base font-black text-[#0F1F3A] mb-3">Tenants ({customers.length})</h3>
                <div className="space-y-1">
                  {customers.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded-lg">
                      <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#00C4CC] to-[#3B82F6] flex items-center justify-center text-white font-black text-[10px] flex-shrink-0">
                        {initials(c.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-black text-[#0F1F3A] truncate">{c.name || "Tenant"}</div>
                        <div className="text-[10px] font-medium text-[#8B93A7] truncate">
                          {c.unit && <>Unit {c.unit}</>}
                          {c.phone && <> · {c.phone}</>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* "All actions & details" bar */}
        <div className="mt-6">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="v2p-open-drawer-bar"
            className="w-full bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-4 flex items-center gap-3 hover:shadow-lg transition-shadow group">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#0F1F3A] to-[#1a2d4f] flex items-center justify-center text-white text-lg flex-shrink-0">⋮</div>
            <div className="text-left flex-1 min-w-0">
              <div className="text-sm font-black text-[#0F1F3A]">All actions &amp; details</div>
              <div className="text-[11px] font-medium text-[#8B93A7]">
                Property Snapshot · Compliance pull · Bulk walk · Street view · Documents · Assets · Appliances
              </div>
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-[#00807a] group-hover:text-[#0F1F3A]">Open →</span>
          </button>
        </div>
      </div>

      {/* Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[100]" data-testid="v2p-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDrawerOpen(false)}/>
          <aside className="absolute right-0 top-0 h-full w-full lg:max-w-[900px] xl:max-w-[1100px] bg-slate-50 shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200">
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-2">
              <div>
                <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">Full toolbox</div>
                <div className="text-sm font-black text-[#0F1F3A]">All actions &amp; details</div>
              </div>
              <button onClick={() => setDrawerOpen(false)} data-testid="v2p-close-drawer"
                className="ml-auto h-9 w-9 rounded-full hover:bg-slate-100 text-[#0F1F3A] text-xl leading-none">×</button>
            </div>
            <div className="bg-white">
              <PropertyDetailBody v2/>
            </div>
          </aside>
        </div>
      )}

      {timelineOpen && (
        <PropertyPhotoTimeline
          address={address}
          onClose={() => setTimelineOpen(false)}
        />
      )}
      {renameOpen && (
        <RenameAddressModal
          from={address}
          onClose={() => setRenameOpen(false)}
          onRenamed={(newAddr) => {
            setRenameOpen(false);
            toast.success("Address updated across all WOs");
            navigate(`/admin/properties/${encodeURIComponent(newAddr)}`);
          }}
        />
      )}
    </AdminShell>
  );
}

/**
 * RenameAddressModal — bulk-rename a property address across every WO,
 * inspection, rent-roll, tenant-directory, and compliance record it
 * touches. Requires the admin to retype the new address to prevent
 * accidental fat-finger renames on a live tenant's data.
 */
function RenameAddressModal({ from, onClose, onRenamed }) {
  const [to, setTo] = useState(from);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const trimmed = to.trim();
  const canSubmit = trimmed.length >= 4
    && trimmed !== from
    && confirm.trim().toLowerCase() === trimmed.toLowerCase();
  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await api.post("/properties/rename-address",
                                { from_address: from, to_address: trimmed });
      const jobs = r.data?.updated?.jobs ?? 0;
      toast.success(`✓ Renamed on ${jobs} WO${jobs === 1 ? "" : "s"}`);
      onRenamed(trimmed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[400] bg-black/80 flex items-center justify-center p-4" onClick={onClose}
         data-testid="prop-rename-modal">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-slate-200">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Correct property address</div>
          <div className="font-display font-black text-xl text-[#0F1F3A] mt-1">Edit Address</div>
          <div className="text-xs text-slate-600 mt-1">
            Renames this address across <b>every WO, inspection, rent-roll, and compliance record</b> that references it. Re-geocodes so the map pin & driving distance are correct.
          </div>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Current (wrong) address</label>
            <div className="h-10 px-3 flex items-center bg-slate-100 border-2 border-slate-200 rounded-lg text-sm font-mono text-slate-700">
              {from}
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Correct address</label>
            <input value={to} onChange={e => setTo(e.target.value)} autoFocus
              data-testid="prop-rename-to"
              placeholder="e.g. 1629 Hicks St"
              className="w-full h-10 px-3 border-2 border-slate-300 focus:border-[#00C4CC] outline-none rounded-lg text-sm"/>
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Retype the correct address to confirm</label>
            <input value={confirm} onChange={e => setConfirm(e.target.value)}
              data-testid="prop-rename-confirm"
              placeholder="Retype exactly as above"
              className="w-full h-10 px-3 border-2 border-slate-300 focus:border-[#00C4CC] outline-none rounded-lg text-sm"/>
          </div>
          <div className="bg-amber-50 border-l-4 border-amber-500 p-2 text-[11px] text-amber-900 leading-relaxed">
            <b>Heads up:</b> this modifies live data on every affected record. Audit log entry is created. Can be re-run with reversed addresses to undo.
          </div>
        </div>
        <div className="p-3 bg-slate-50 border-t-2 border-slate-200 flex gap-2 justify-end">
          <button onClick={onClose} data-testid="prop-rename-cancel"
            className="h-10 px-4 text-slate-600 hover:text-[#0F1F3A] text-sm font-black uppercase tracking-widest">Cancel</button>
          <button onClick={submit} disabled={!canSubmit || busy} data-testid="prop-rename-save"
            className="h-10 px-4 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-black uppercase tracking-widest rounded-lg">
            {busy ? "Renaming…" : "Rename Address"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * PropertyPhotoTimeline — chronological photo waterfall across every
 * WO ever done at this address. Insurance / legal proof gold.
 */
function PropertyPhotoTimeline({ address, onClose }) {
  const [photos, setPhotos] = useState(null);
  const [lb, setLb] = useState(null);
  useEffect(() => {
    api.get("/properties/photos/timeline", { params: { address } })
      .then(r => setPhotos(r.data?.photos || []))
      .catch(() => setPhotos([]));
  }, [address]);
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
  };
  // Group by month for the waterfall header
  const groups = React.useMemo(() => {
    const m = new Map();
    (photos || []).forEach(p => {
      const k = (p.at || "").slice(0, 7) || "unknown";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(p);
    });
    return Array.from(m.entries());
  }, [photos]);
  return (
    <div className="fixed inset-0 z-[200] bg-black/85 flex flex-col" onClick={onClose} data-testid="property-timeline-modal">
      <div className="p-4 flex items-center gap-2 border-b border-white/10" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="h-9 w-9 flex items-center justify-center text-white hover:bg-white/10 rounded" data-testid="property-timeline-close">×</button>
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-[#7FE8ED]">Photo Timeline</div>
          <div className="font-display font-black text-lg text-white">{address}</div>
        </div>
        <div className="ml-auto text-xs text-white/60 font-bold">
          {photos ? `${photos.length} photo${photos.length === 1 ? "" : "s"}` : "Loading…"}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        {!photos && <div className="text-white/60 text-sm">Loading timeline…</div>}
        {photos && photos.length === 0 && (
          <div className="text-center py-20 text-white/70">
            <div className="text-5xl mb-3">📷</div>
            <div className="font-display font-black text-xl">No photos on any WO here yet</div>
          </div>
        )}
        {groups.map(([month, items]) => (
          <div key={month} className="mb-8">
            <h2 className="text-white/80 font-display font-black text-xl mb-3 border-b border-white/10 pb-2">
              {month === "unknown" ? "Unknown date" : new Date(month + "-01").toLocaleDateString([], { month: "long", year: "numeric" })}
              <span className="ml-3 text-xs font-black text-white/40 uppercase tracking-widest">{items.length} photo{items.length === 1 ? "" : "s"}</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {items.map((p, i) => (
                <button key={i} onClick={() => setLb(p)}
                  data-testid={`property-timeline-tile-${month}-${i}`}
                  className="group relative aspect-square rounded-xl overflow-hidden bg-white/5 hover:ring-2 hover:ring-[#00C4CC] transition">
                  <img src={`${API.replace(/\/api$/, "")}${p.url}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                  <span className="absolute top-1.5 right-1.5 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-white/90 text-[#0F1F3A] capitalize">
                    {p.kind}
                  </span>
                  {(p.caption || (p.ai_tags?.length > 0)) && (
                    <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-gradient-to-t from-[#0F1F3A]/95 to-transparent text-[10px] font-black text-white line-clamp-2 leading-tight">
                      {p.caption || p.ai_tags.slice(0, 2).join(" · ")}
                    </div>
                  )}
                  <div className="absolute top-1.5 left-1.5 text-[8px] font-black text-white/90 bg-black/50 rounded px-1 py-0.5">
                    {fmt(p.at).split(",")[0]}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {lb && (
        <div className="fixed inset-0 z-[210] bg-black/95 flex items-center justify-center p-4" onClick={() => setLb(null)}>
          <div onClick={(e) => e.stopPropagation()} className="max-w-4xl w-full">
            <img src={`${API.replace(/\/api$/, "")}${lb.url}`} alt="" className="max-h-[75vh] mx-auto rounded-2xl"/>
            <div className="mt-3 text-center text-white/90">
              {lb.caption && <div className="font-black text-lg">{lb.caption}</div>}
              <div className="text-sm text-white/60 capitalize mt-1">
                {lb.kind} · {fmt(lb.at)}{lb.work_order_number ? ` · WO ${lb.work_order_number}` : ""}
              </div>
              <Link to={`/admin/jobs/${lb.job_id}`} className="mt-3 inline-flex items-center gap-1 h-9 px-4 rounded-full bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0F1F3A] font-black uppercase tracking-widest text-xs">
                Open WO →
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* helpers */
function Card({ children, testId, pad = "md", className = "" }) {
  const p = pad === "sm" ? "p-3" : pad === "lg" ? "p-6" : "p-5";
  return <div data-testid={testId} className={`bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] ${p} ${className}`}>{children}</div>;
}
function StatTile({ label, value, color, to }) {
  const inner = (
    <>
      <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">{label}</div>
      <div className="text-2xl sm:text-3xl font-black mt-1 tabular-nums" style={{ color }}>{value}</div>
    </>
  );
  const cls = "bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow border border-slate-100 block";
  return to ? <Link to={to} className={cls}>{inner}</Link> : <div className={cls}>{inner}</div>;
}
function ActionTile({ icon, label, onClick, disabled, testid }) {
  return (
    <button onClick={onClick} disabled={disabled}
      data-testid={testid}
      className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-colors ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-50"}`}>
      <span className="text-xl leading-none">{icon}</span>
      <span className="text-[9px] font-black uppercase tracking-widest text-[#0F1F3A]">{label}</span>
    </button>
  );
}
function ComplianceRow({ label, value, color }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: color }}/>
      <span className="text-[#8B93A7] font-medium">{label}</span>
      <span className="ml-auto font-black" style={{ color }}>{value}</span>
    </div>
  );
}
function initials(name) {
  if (!name) return "?";
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();
}
function fmtDate(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return ""; }
}
